import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  InsertDriveFile as FileIcon,
  Warning as WarningIcon,
  Send as LoadIcon,
} from '@mui/icons-material';
import Papa from '@signals/app-shell/shims/papaparse';
import type { ParseResult, ParseError } from '@signals/app-shell/shims/papaparse';
import { PathfinderDataPoint } from '../../services/pathfinderApi';

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  uploadedDate: Date;
  data?: any[];
  error?: string;
  loaded?: boolean;
  visible?: boolean;
}

interface CSVUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onDataLoaded: (dataPoints: PathfinderDataPoint[], fileName: string) => void;
  onToggleVisibility?: (fileName: string, visible: boolean) => void;
}

const ENTITY_THRESHOLD = 500;
const POINTS_THRESHOLD = 50000;

const findProperty = (props: any, possibleNames: string[]): any => {
  const lowerProps: Record<string, any> = {};
  Object.keys(props).forEach(key => {
    lowerProps[key.toLowerCase()] = props[key];
  });

  for (const name of possibleNames) {
    const value = lowerProps[name.toLowerCase()];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
};

const parseCSV = async (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<any>) => {
        if (results.errors.length > 0) {
          reject(new Error(`CSV parsing errors: ${results.errors.map((e: ParseError) => e.message).join(', ')}`));
        } else {
          resolve(results.data);
        }
      },
      error: (error: Error) => {
        reject(error);
      },
    });
  });
};

const parseGeoJSON = async (file: File): Promise<any[]> => {
  const text = await file.text();
  const geoJsonData = JSON.parse(text);
  if (!geoJsonData.type || geoJsonData.type !== 'FeatureCollection') {
    throw new Error('Invalid GeoJSON format: Missing or invalid "type" field');
  }
  if (!geoJsonData.features || !Array.isArray(geoJsonData.features)) {
    throw new Error('Invalid GeoJSON format: Missing or invalid "features" array');
  }
  return geoJsonData.features;
};

const parseKML = async (file: File): Promise<any[]> => {
  const text = await file.text();
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(text, 'text/xml');

  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Invalid KML file: XML parsing error');
  }

  const features: any[] = [];
  const placemarks = xmlDoc.querySelectorAll('Placemark');

  if (placemarks.length === 0) {
    throw new Error('No Placemarks found in KML file');
  }

  placemarks.forEach((placemark, index) => {
    try {
      const nameEl = placemark.querySelector('name');
      const descEl = placemark.querySelector('description');
      const name = nameEl?.textContent?.trim() || `Placemark-${index}`;
      const description = descEl?.textContent?.trim() || '';

      const extendedData: Record<string, string> = {};
      const dataElements = placemark.querySelectorAll('ExtendedData Data, ExtendedData SimpleData');
      dataElements.forEach(dataEl => {
        const dataName = dataEl.getAttribute('name') || dataEl.getAttribute('key');
        const dataValue = dataEl.textContent?.trim();
        if (dataName && dataValue) {
          extendedData[dataName] = dataValue;
        }
      });

      const schemaData = placemark.querySelectorAll('ExtendedData SchemaData SimpleData');
      schemaData.forEach(dataEl => {
        const dataName = dataEl.getAttribute('name');
        const dataValue = dataEl.textContent?.trim();
        if (dataName && dataValue) {
          extendedData[dataName] = dataValue;
        }
      });

      let timestamp: string | null = null;
      const timeStampEl = placemark.querySelector('TimeStamp when');
      const timeSpanBegin = placemark.querySelector('TimeSpan begin');
      if (timeStampEl?.textContent) {
        timestamp = timeStampEl.textContent.trim();
      } else if (timeSpanBegin?.textContent) {
        timestamp = timeSpanBegin.textContent.trim();
      }

      // Point geometry
      const pointEl = placemark.querySelector('Point coordinates');
      if (pointEl?.textContent) {
        const coords = pointEl.textContent.trim().split(',').map(c => parseFloat(c.trim()));
        if (coords.length >= 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [coords[0], coords[1], coords[2] || 0] },
            properties: { name, description, timestamp, ...extendedData },
          });
        }
        return;
      }

      // LineString geometry
      const lineEl = placemark.querySelector('LineString coordinates');
      if (lineEl?.textContent) {
        const coordPairs = lineEl.textContent.trim().split(/\s+/);
        const coordinates = coordPairs.map(pair => {
          const [lng, lat, alt] = pair.split(',').map(c => parseFloat(c.trim()));
          return [lng, lat, alt || 0];
        }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));

        if (coordinates.length > 0) {
          features.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates },
            properties: { name, description, timestamp, ...extendedData },
          });
        }
        return;
      }

      // Polygon geometry
      const polygonEl = placemark.querySelector('Polygon outerBoundaryIs LinearRing coordinates');
      if (polygonEl?.textContent) {
        const coordPairs = polygonEl.textContent.trim().split(/\s+/);
        const coordinates = coordPairs.map(pair => {
          const [lng, lat, alt] = pair.split(',').map(c => parseFloat(c.trim()));
          return [lng, lat, alt || 0];
        }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));

        if (coordinates.length > 0) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [coordinates] },
            properties: { name, description, timestamp, ...extendedData },
          });
        }
        return;
      }

      // MultiGeometry (take first point)
      const multiGeoPoint = placemark.querySelector('MultiGeometry Point coordinates');
      if (multiGeoPoint?.textContent) {
        const coords = multiGeoPoint.textContent.trim().split(',').map(c => parseFloat(c.trim()));
        if (coords.length >= 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [coords[0], coords[1], coords[2] || 0] },
            properties: { name, description, timestamp, ...extendedData },
          });
        }
      }
    } catch (err) {
      console.warn(`Failed to parse Placemark ${index}:`, err);
    }
  });

  if (features.length === 0) {
    throw new Error('No valid geometries found in KML file');
  }

  return features;
};

const calculateCentroid = (coordinates: number[][][]): [number, number] => {
  const points = coordinates[0];
  let sumLng = 0;
  let sumLat = 0;
  const numPoints = points.length - 1;

  for (let i = 0; i < numPoints; i++) {
    sumLng += points[i][0];
    sumLat += points[i][1];
  }
  return [sumLng / numPoints, sumLat / numPoints];
};

const normalizeData = (rawData: any[], format: 'csv' | 'geojson', fileName: string): any[] => {
  return rawData.map((item, index) => {
    let latitude: number = 0;
    let longitude: number = 0;
    let properties: any = {};
    let geometryType: string = 'Point';
    let fullGeometry: any = null;

    if (format === 'csv') {
      latitude = parseFloat(findProperty(item, ['latitude', 'lat', 'y', 'lat_deg', 'lat_decimal']));
      longitude = parseFloat(findProperty(item, ['longitude', 'lon', 'lng', 'long', 'x']));
      properties = { ...item };
    } else if (format === 'geojson') {
      const geometry = item.geometry;
      geometryType = geometry.type;
      fullGeometry = geometry;
      properties = item.properties || {};

      let coordinates: [number, number] | null = null;

      if (geometry.type === 'Point' && geometry.coordinates) {
        coordinates = [geometry.coordinates[0], geometry.coordinates[1]];
      } else if (geometry.type === 'MultiPoint' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0) {
        coordinates = [geometry.coordinates[0][0], geometry.coordinates[0][1]];
      } else if (geometry.type === 'LineString' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0) {
        coordinates = [geometry.coordinates[0][0], geometry.coordinates[0][1]];
      } else if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0) {
        coordinates = calculateCentroid(geometry.coordinates);
      } else if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0 && geometry.coordinates[0][0].length > 0) {
        coordinates = calculateCentroid(geometry.coordinates[0]);
      } else if (geometry.type === 'MultiLineString' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0) {
        coordinates = [geometry.coordinates[0][0][0], geometry.coordinates[0][0][1]];
      }

      if (!coordinates) {
        throw new Error(`Unsupported or invalid geometry type: ${geometry.type} at feature index ${index}`);
      }
      longitude = coordinates[0];
      latitude = coordinates[1];
    }

    const timestamp = findProperty(properties, ['timestamp', 'time', 'date']);
    const deviceId = findProperty(properties, ['deviceId', 'device_id', 'registration_id', 'registration id', 'id', 'device']);
    const accuracy = findProperty(properties, ['accuracy', 'acc', 'accuracy (m)', 'accuracy_m']);
    const deviceType = findProperty(properties, ['deviceType', 'device_type', 'type']);
    const speed = findProperty(properties, ['speed', 'velocity', 'speed_kmh', 'speed_mph', 'spd']);
    const altitude = findProperty(properties, ['altitude', 'alt', 'elevation']);

    return {
      longitude,
      latitude,
      timestamp: timestamp || new Date().toISOString(),
      deviceId: String(deviceId || `${fileName.replace(/\.(csv|geojson|json|kml)$/i, '')}-point-${index}`),
      accuracy: accuracy || null,
      deviceType: String(deviceType || 'phone'),
      speed: speed || null,
      altitude: altitude || null,
      ...properties,
      _fileFormat: format,
      _originalIndex: index,
      _geometryType: geometryType,
      _fullGeometry: fullGeometry,
    };
  });
};

const CSVUploadDialog: React.FC<CSVUploadDialogProps> = ({ open, onClose, onDataLoaded, onToggleVisibility }) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const [showWarning, setShowWarning] = useState(false);
  const [pendingLoad, setPendingLoad] = useState<{ file: UploadedFile; dataPoints: PathfinderDataPoint[]; entityCount: number } | null>(null);

  const handleToggleVisibility = useCallback((fileId: string) => {
    setUploadedFiles(prev => prev.map(f => {
      if (f.id === fileId && f.loaded) {
        const newVisible = !f.visible;
        if (onToggleVisibility) {
          onToggleVisibility(f.name, newVisible);
        }
        return { ...f, visible: newVisible };
      }
      return f;
    }));
  }, [onToggleVisibility]);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setLoading(true);
    setError(null);
    setProgress(0);

    const newFiles: UploadedFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = `${file.name}-${Date.now()}-${i}`;

      try {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (!fileExtension) {
          throw new Error(`Could not determine file extension for ${file.name}`);
        }

        let rawData: any[] = [];
        let format: 'csv' | 'geojson';

        if (fileExtension === 'csv') {
          rawData = await parseCSV(file);
          format = 'csv';
        } else if (fileExtension === 'geojson' || fileExtension === 'json') {
          rawData = await parseGeoJSON(file);
          format = 'geojson';
        } else if (fileExtension === 'kml') {
          rawData = await parseKML(file);
          format = 'geojson';
        } else {
          throw new Error(`Unsupported file type: ${fileExtension}. Supported: CSV, GeoJSON, JSON, KML.`);
        }

        const parsedData = normalizeData(rawData, format, file.name);

        newFiles.push({
          id: fileId,
          file,
          name: file.name,
          size: file.size,
          uploadedDate: new Date(),
          data: parsedData,
        });

        setProgress(((i + 1) / files.length) * 100);
      } catch (err: any) {
        newFiles.push({
          id: fileId,
          file,
          name: file.name,
          size: file.size,
          uploadedDate: new Date(),
          error: err.message || 'Failed to parse file',
        });
        setError(`Error parsing ${file.name}: ${err.message}`);
      }
    }

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setLoading(false);
    event.target.value = '';
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const handleLoadFile = useCallback(
    (file: UploadedFile) => {
      if (!file.data || file.data.length === 0) {
        setError('No data found in file');
        return;
      }

      try {
        const firstRow = file.data[0];
        const isGeoJSON = firstRow._fileFormat === 'geojson';

        let latColumn = '';
        const latOptions = ['latitude', 'lat', 'y', 'lat_deg', 'lat_decimal'];
        for (const opt of latOptions) {
          const found = Object.keys(firstRow).find(k => k.toLowerCase().trim() === opt);
          if (found) { latColumn = found; break; }
        }

        let lngColumn = '';
        const lngOptions = ['longitude', 'lon', 'lng', 'long', 'x', 'lon_deg', 'lng_decimal', 'long_decimal'];
        for (const opt of lngOptions) {
          const found = Object.keys(firstRow).find(k => k.toLowerCase().trim() === opt);
          if (found) { lngColumn = found; break; }
        }

        if (!isGeoJSON && (!latColumn || !lngColumn)) {
          setError(`Could not detect coordinate columns. Found: ${Object.keys(firstRow).join(', ')}. Please ensure your file has columns named: latitude/lat/y and longitude/lon/lng/x`);
          return;
        }

        const uploadTime = new Date();

        let skippedRows = 0;
        const dataPoints: PathfinderDataPoint[] = [];

        for (let index = 0; index < file.data.length; index++) {
          const row = file.data[index];
          let lat: number, lng: number;

          if (isGeoJSON) {
            lat = parseFloat(row.latitude);
            lng = parseFloat(row.longitude);
          } else {
            lat = parseFloat(row[latColumn]);
            lng = parseFloat(row[lngColumn]);
          }

          if (isNaN(lat) || isNaN(lng)) {
            skippedRows++;
            continue;
          }

          const timestampRaw = row.timestamp || row.time || row.Timestamp || row.Time || row.date || row.Date;

          let timestamp = uploadTime;
          if (timestampRaw) {
            try {
              timestamp = new Date(timestampRaw);
              if (isNaN(timestamp.getTime())) {
                timestamp = uploadTime;
              }
            } catch {
              timestamp = uploadTime;
            }
          }

          const deviceId = row.deviceId || row.device_id || row.registration_id || row.id || row.ID || row.device || row.Device || `upload-${index}`;
          const studentId = row.studentId || row.student_id || row.entity_id || row.entityId || row.user_id || row.userId || deviceId;
          const accuracy = parseFloat(row.accuracy || row.Accuracy || row.acc || '10');
          const deviceType = row.deviceType || row.device_type || row.type || row.Type || 'phone';

          const { _fullGeometry, _geometryType, _fileFormat, _originalIndex, longitude, latitude, ...cleanRow } = row;

          dataPoints.push({
            id: `${file.name}-${index}`,
            coordinates: [lat, lng] as [number, number],
            timestamp,
            studentId,
            deviceId,
            deviceType: deviceType as 'phone' | 'watch' | 'vehicle',
            accuracy: isNaN(accuracy) ? 10 : accuracy,
            speed: parseFloat(row.speed || row.Speed || '0') || undefined,
            metadata: {
              ...cleanRow,
              originalFileName: file.name,
              geometryType: _geometryType,
              source: 'csv-upload',
              altitude: parseFloat(row.altitude || row.Altitude || row.alt || '0') || undefined,
            },
          });
        }

        if (dataPoints.length === 0) {
          setError('No rows with valid coordinates found in file');
          return;
        }

        if (skippedRows > 0) {
          setWarning(`Loaded ${dataPoints.length} points (skipped ${skippedRows} rows with missing coordinates)`);
        }

        const uniqueEntities = new Set(dataPoints.map(p => p.deviceId)).size;
        const pointCount = dataPoints.length;

        if (uniqueEntities > ENTITY_THRESHOLD || pointCount > POINTS_THRESHOLD) {
          setPendingLoad({ file, dataPoints, entityCount: uniqueEntities });
          setShowWarning(true);
        } else {
          setUploadedFiles(prev => prev.map(f =>
            f.id === file.id ? { ...f, loaded: true, visible: true } : f
          ));
          onDataLoaded(dataPoints, file.name);
        }
      } catch (err: any) {
        setError(`Error processing file: ${err.message}`);
      }
    },
    [onDataLoaded]
  );

  const handleProceedWithWarning = useCallback(() => {
    if (pendingLoad) {
      setUploadedFiles(prev => prev.map(f =>
        f.id === pendingLoad.file.id ? { ...f, loaded: true, visible: true } : f
      ));
      onDataLoaded(pendingLoad.dataPoints, pendingLoad.file.name);
      setShowWarning(false);
      setPendingLoad(null);
    }
  }, [pendingLoad, onDataLoaded]);

  const handleCancelWarning = useCallback(() => {
    setShowWarning(false);
    setPendingLoad(null);
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const totalSize = uploadedFiles.reduce((sum, file) => sum + file.size, 0);
  const maxTotalSize = 100 * 1024 * 1024;
  const maxFiles = 10;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <UploadIcon />
          <Typography variant="h6">Upload Dataset</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 1.5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            CSV, GeoJSON, JSON, KML | Max {maxFiles} files, 100MB
          </Typography>
          <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
            {uploadedFiles.length}/{maxFiles} files, {formatFileSize(totalSize)}/100MB
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {warning && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
            {warning}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
              Processing... {progress.toFixed(0)}%
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          component="label"
          startIcon={<UploadIcon />}
          fullWidth
          disabled={loading || uploadedFiles.length >= maxFiles || totalSize >= maxTotalSize}
          sx={{ mb: 2, py: 1 }}
          size="small"
        >
          Select Files
          <input
            type="file"
            hidden
            multiple
            accept=".csv,.geojson,.json,.kml"
            onChange={handleFileChange}
          />
        </Button>

        {uploadedFiles.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Files ({uploadedFiles.length})
            </Typography>
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              {uploadedFiles.map((file) => (
                <ListItem
                  key={file.id}
                  secondaryAction={
                    <Box>
                      {!file.error && !file.loaded && (
                        <IconButton
                          edge="end"
                          aria-label="load to map"
                          onClick={() => handleLoadFile(file)}
                          color="primary"
                          sx={{ mr: 1 }}
                        >
                          <LoadIcon />
                        </IconButton>
                      )}
                      {!file.error && file.loaded && (
                        <IconButton
                          edge="end"
                          aria-label="toggle visibility"
                          onClick={() => handleToggleVisibility(file.id)}
                          color={file.visible ? 'primary' : 'default'}
                          sx={{ mr: 1 }}
                        >
                          {file.visible ? <ViewIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemoveFile(file.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <FileIcon color={file.error ? 'error' : file.loaded ? 'success' : 'primary'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={
                      <>
                        {file.error ? (
                          <Typography variant="caption" color="error">
                            {file.error}
                          </Typography>
                        ) : (
                          <>
                            Uploaded: {file.uploadedDate.toLocaleDateString()} | {formatFileSize(file.size)}
                            {file.data && ` | ${file.data.length} rows`}
                            {file.loaded && (
                              <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                                • Loaded {file.visible ? '(Visible)' : '(Hidden)'}
                              </Typography>
                            )}
                          </>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Warning Dialog for Large Datasets */}
      <Dialog
        open={showWarning}
        onClose={handleCancelWarning}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningIcon />
          Large Dataset Warning
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This file contains a large number of entities or points. Loading this much data can severely degrade performance. Do you wish to proceed?
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="primary">
              Entities: {pendingLoad?.entityCount} (threshold: {ENTITY_THRESHOLD})
            </Typography>
            <Typography variant="body2" color="primary">
              Points: {pendingLoad?.dataPoints.length} (threshold: {POINTS_THRESHOLD})
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelWarning} variant="outlined" color="info">
            Cancel
          </Button>
          <Button onClick={handleProceedWithWarning} variant="contained" color="warning">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CSVUploadDialog;
